import { findLastIndex } from 'lodash'
import type { MutableRefObject } from 'react'

// Buffer to account for timing gap in merged audio after transfer
export const TRANSFER_BUFFER = 6

export const SPEAKER_PREFIX_PATTERN = /\[SPEAKER_\d+\]|\[(ai|office|caller)\]/i

export interface TranscriptLine {
  id: string
  text: string
  startSeconds: number
  endSeconds: number
  startTime: string
  endTime: string
}

export interface ProcessedTranscriptLine extends TranscriptLine {
  adjustedStart: number
  isTransferLine: boolean
  textBeforeTransfer: string | null
}

export interface ProcessedTranscript {
  lines: ProcessedTranscriptLine[]
  transferIndex: number
  timeOffset: number
}

/**
 * Processes transcript lines to calculate adjusted timestamps for post-transfer segments.
 * The merged audio has a timing gap after transfer that needs to be accounted for.
 */
export function processTranscriptWithTransfers(lines: TranscriptLine[]): ProcessedTranscript {
  let transferIndex = -1
  let timeOffset = 0

  const processedLines = lines.map((line, i) => {
    // Calculate adjusted time FIRST using CURRENT transferIndex/timeOffset
    const isFirstPostTransfer = i === transferIndex + 1 && transferIndex >= 0
    const isPostTransfer = transferIndex >= 0 && i > transferIndex

    const adjustedStart = isFirstPostTransfer
      ? timeOffset
      : isPostTransfer
        ? line.startSeconds + timeOffset
        : line.startSeconds

    // THEN check for transfer and update for NEXT iterations
    const transferredMatch = line.text.match(/\[transferred\]/i)
    if (transferredMatch) {
      transferIndex = i
      const nextLine = lines[i + 1]
      const firstPostTransferStart = nextLine?.startSeconds || 0
      timeOffset = line.endSeconds + firstPostTransferStart + TRANSFER_BUFFER
    }

    const textBeforeTransfer = transferredMatch ? line.text.slice(0, transferredMatch.index).trim() : null

    return {
      ...line,
      adjustedStart,
      isTransferLine: !!transferredMatch,
      textBeforeTransfer,
    }
  })

  return { lines: processedLines, transferIndex, timeOffset }
}

/**
 * Places agent tool calls in the transcript: each goes after the last line that
 * starts at or before it, keyed by that line's index, or -1 when it comes before
 * every line. Lines after a transfer are skipped, since the agent has left the
 * call by then. A call with no time goes after the agent's last line.
 */
export function placeToolCalls<T extends { timeInCallSecs?: number }>(
  lines: TranscriptLine[],
  transferIndex: number,
  toolCalls: T[],
): Map<number, T[]> {
  const agentLines = transferIndex >= 0 ? lines.slice(0, transferIndex + 1) : lines
  const placed = new Map<number, T[]>()
  for (const toolCall of toolCalls) {
    const time = toolCall.timeInCallSecs ?? Infinity
    const index = findLastIndex(agentLines, (line) => line.startSeconds <= time)
    placed.set(index, [...(placed.get(index) ?? []), toolCall])
  }
  return placed
}

/**
 * Finds the active transcript line index based on current audio playback time.
 */
export function findActiveTranscriptLineIndex(
  lines: ProcessedTranscriptLine[],
  currentTime: number,
  transferIndex: number,
  timeOffset: number,
): number {
  const flooredTime = Math.floor(currentTime)

  return lines.findIndex((line, i) => {
    const isFirstPostTransfer = i === transferIndex + 1
    const isPostTransfer = transferIndex >= 0 && i > transferIndex

    const displayedStart = isFirstPostTransfer
      ? Math.floor(timeOffset)
      : isPostTransfer
        ? Math.floor(line.startSeconds + timeOffset)
        : Math.floor(line.startSeconds)

    const nextLine = lines[i + 1]
    const isNextFirstPostTransfer = i + 1 === transferIndex + 1
    const isNextPostTransfer = transferIndex >= 0 && i + 1 > transferIndex

    const displayedEnd = nextLine
      ? isNextFirstPostTransfer
        ? Math.floor(timeOffset)
        : isNextPostTransfer
          ? Math.floor(nextLine.startSeconds + timeOffset)
          : Math.floor(nextLine.startSeconds)
      : Infinity

    return displayedStart <= flooredTime && flooredTime < displayedEnd
  })
}

/**
 * Updates transcript line highlighting based on current playback time.
 */
export function updateTranscriptHighlight(
  containerRef: MutableRefObject<HTMLDivElement | null>,
  activeLineIndex: number,
): void {
  if (!containerRef.current) return

  containerRef.current.querySelectorAll(`[data-line-index]`).forEach((element) => {
    const lineIndex = parseInt(element.getAttribute(`data-line-index`)!, 10)
    if (lineIndex === activeLineIndex) {
      element.classList.add(`bg-blue-100`)
    } else {
      element.classList.remove(`bg-blue-100`)
    }
  })
}

export function formatSeconds(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${String(mins).padStart(2, `0`)}:${String(secs).padStart(2, `0`)}`
}

export function getSpeakerNumber(lineText: string): number | null {
  const match = lineText.match(/\[SPEAKER_(\d+)\]/)
  if (match) {
    return parseInt(match[1], 10)
  }
  return null
}

export function formatSpeaker(lineText: string): string {
  let match = lineText.match(/\[SPEAKER_(\d+)\]/)
  if (match) {
    const speakerNumber = parseInt(match[1], 10)
    return `Speaker ${speakerNumber}`
  }
  match = lineText.match(/\[(ai|office|caller)\]/i)
  if (match) return match[1]

  return `Unknown speaker`
}

export function getSpeakerColor(lineText: string): string {
  const speakerNumber = getSpeakerNumber(lineText)
  switch (speakerNumber) {
    case 1:
      return `#3B82F6`
    case 2:
      return `#33A784`
    case 3:
      return `#FCB900`
    case 4:
      return `#D900FC`
    case 5:
      return `#FC4C00`
    default:
      return `#A7A9AC`
  }
}

export function stripSpeakerPrefix(text: string): string {
  return text.replace(/^\[SPEAKER_(\d+)\]: /, ``).replace(/\[(ai|office|caller)\]: /i, ``)
}
